// Kanemori Sanctuary boundary wall (Cr3L, 2026-10-18: "where this new area meets the city it doesn't look right;
// a tall stone wall like I've seen protecting temples"). The two solid columns x256-257 of the whole west edge are one
// temple wall, painted from above in the 2x art: an ishigaki battered stone base of big fitted irregular stones on both
// faces, a white plaster tsuiji face with dark timber posts, a deep eave shadow and a grey tiled cap with a ridge. The
// city side (west) is lit and clean, the sanctuary side (east) is the shaded inside; north of the ridge (the monastery)
// the same wall is old: moss, fallen plaster, broken cap tiles. The road opening at y84-86 is framed by two gate piers
// (a lodge with a front facing the road, and a roofed block), with a granite sill laid across the road (hd-sanctuary-place.ts).
// Presentation only: the tiles under it were already solid; nothing here moves collision or a gameplay point.
// Palette: hd-kit ramps of theme.ts only.
import { hash2 } from '../../common/src/util';
import { HT, K, type G, px, speckle, dk, lt, mix, alpha, text, textW } from './hd-kit';
import { registerFront, registerRoof } from './hd-registry';
import { lantern } from './hd-parts';
import { SK, NEW_TILE, OLD_TILE, hipRoof, visible, sanctuaryOrigin } from './hd-sanctuary';

/** wall layout across its 64 hd px (two tiles), west (city) to east (inside) */
const W = {
  inkW: 0, stoneW: 1, stoneN: 8, plasterW: 9, plasterN: 16, shadeW: 25, shadeN: 2,
  capW: 27, capN: 16, shadeE: 43, plasterE: 45, plasterEN: 12, stoneE: 57, stoneEN: 6,
};
const POST_EVERY = 128; // a timber post every four tiles
const PIER_EVERY = 256; // a roofed pier (a pilaster with its own little roof and a south face) every eight tiles
const STONE_ROWS = 150 * HT;

const cuts = new Map<number, number[]>();
/** stone joints along the wall in world hd px for one face, irregular lengths fitted end to end */
function joints(face: number): number[] {
  let b = cuts.get(face); if (b) return b;
  b = [0]; let y = 0, i = 0;
  while (y < STONE_ROWS) { y += 14 + Math.floor(hash2(face * 31 + i, 7 + face) * 24); b.push(y); i++; }
  cuts.set(face, b); return b;
}

/** one face of the battered stone base: large irregular stones, lighter toward the wall, darker where the slope meets the ground */
function stoneFace(g: G, x: number, y0: number, y1: number, wy0: number, face: number, old: boolean, inside: boolean, n = W.stoneN) {
  const b = joints(face);
  let i = 0; while (i + 1 < b.length && b[i + 1] <= wy0) i++;
  for (; i + 1 < b.length && b[i] < wy0 + (y1 - y0); i++) {
    const a = b[i], z = b[i + 1], v = hash2(face * 97 + i, 3), j = hash2(i, face + 11);
    const ly = Math.max(y0, y0 + a - wy0), le = Math.min(y1, y0 + z - wy0); if (le <= ly) continue;
    let c = v < 0.22 ? SK.stoneHi : v < 0.7 ? SK.stone : SK.stoneLo;
    if (old) c = mix(c, SK.mossLo, 0.3 + 0.2 * j);
    if (inside) c = dk(c, 0.22);
    const lead = Math.floor(j * 3), len = le - ly; // some stones stand a little proud of the batter
    px(g, x, ly, n, len, K.ink); px(g, x + lead, ly + 1, n - lead, len - 1, c);
    px(g, x + lead, ly + 1, n - lead, 1, lt(c, inside ? 0.08 : 0.2)); // lit upper edge
    px(g, x + lead, ly + 1, 1, len - 1, lt(c, inside ? 0.05 : 0.14)); // lit west edge
    px(g, x + lead, le - 2, n - lead, 1, dk(c, 0.22)); // shaded lower edge
    if (z - a > 24 && v > 0.5) px(g, x + lead + 3 + Math.floor(j * 4), ly + 4 + Math.floor(j * 6), 1, Math.max(1, len - 9), dk(c, 0.3)); // a crack
    if (a > 0 && le - ly > 3) px(g, x, ly, n, 1, K.ink);
  }
  // the batter: the outer foot is in shadow, the upper stones catch the light
  const out = inside ? x + n - 1 : x;
  for (let k = 0; k < 10; k++) {
    const al = (inside ? 0.36 : 0.3) * (1 - k / 10) ** 1.6, xx = inside ? out - k : out + k;
    px(g, xx, y0, 1, y1 - y0, alpha(K.night, al));
  }
  px(g, inside ? x : x + n - 1, y0, 1, y1 - y0, alpha(K.white, inside ? 0.04 : 0.1));
  speckle(g, x, y0, n, y1 - y0, old ? SK.moss : SK.moss, old ? 0.06 : 0.02, face * 5 + 2, 2);
}

/** a plaster face strip: white lime plaster, weathered; posts and patches anchored to the world so rects join */
function plasterFace(g: G, x: number, w: number, y0: number, y1: number, wy0: number, face: number, old: boolean, inside: boolean) {
  const base = inside ? dk(SK.paper, 0.2) : SK.paper;
  px(g, x, y0, w, y1 - y0, base);
  speckle(g, x, y0, w, y1 - y0, alpha(SK.stoneLo, 0.4), old ? 0.08 : 0.04, face * 3 + 1, 2);
  // stains in patches, fallen plaster (old) showing the clay and timber core
  const S = 48;
  for (let s = Math.floor((wy0 - 40) / S); s <= Math.floor((wy0 + (y1 - y0)) / S); s++) {
    const v = hash2(s * 7 + face, 17), len = 10 + Math.floor(hash2(s, face + 4) * 22), at = s * S + Math.floor(hash2(face, s * 3) * 30);
    const ly = y0 + at - wy0; if (ly + len < y0 || ly > y1) continue;
    const a = Math.max(y0, ly), e = Math.min(y1, ly + len);
    if (old && v < 0.4) { px(g, x, a, w, e - a, mix(K.wood, K.slate, 0.5)); speckle(g, x, a, w, e - a, SK.mossLo, 0.3, s, 1); px(g, x, a, w, 1, dk(base, 0.3)); }
    else if (v < (old ? 0.8 : 0.3)) px(g, x, a, w, e - a, mix(base, SK.ashLo, old ? 0.4 : 0.22));
  }
  // the face reads as upright wall: darker toward the eave where the cap throws its shadow, a lighter lit middle, and
  // two timber rails (a low dado and the lintel under the eave) running the whole length
  for (let k = 0; k < w; k++) {
    const t = inside ? 1 - k / (w - 1) : k / (w - 1); // 0 at the stone, 1 at the eave
    px(g, x + k, y0, 1, y1 - y0, alpha(K.night, 0.34 * t ** 2.2));
  }
  const lx = inside ? x : x + w - 4, dx = inside ? x + w - 2 : x;
  px(g, lx, y0, 4, y1 - y0, old ? SK.ashLo : K.woodLo); px(g, inside ? lx + 4 : lx - 1, y0, 1, y1 - y0, K.ink); // lintel rail under the eave
  px(g, dx, y0, 2, y1 - y0, dk(old ? SK.ashLo : K.wood, 0.2)); // dado rail above the stone
  if (!inside) px(g, x + 4, y0, 1, y1 - y0, alpha(K.white, 0.16)); // sun catching the lime
  // timber posts
  for (let p = Math.floor((wy0 - 6) / POST_EVERY); p <= Math.floor((wy0 + (y1 - y0) + 6) / POST_EVERY); p++) {
    const ly = y0 + p * POST_EVERY - wy0 - 4; if (ly + 9 < y0 || ly > y1) continue;
    const a = Math.max(y0, ly), e = Math.min(y1, ly + 9), ink = K.ink, wood = old ? SK.ashLo : K.woodLo, hi = old ? SK.ashHi : K.wood;
    px(g, x - 1, a, w + 2, e - a, ink); px(g, x, a + (a === ly ? 1 : 0), w, e - a - (a === ly ? 1 : 0) - (e === ly + 9 ? 1 : 0), wood);
    if (a === ly) px(g, x, a + 1, w, 1, hi);
  }
}

/** the tiled cap seen from above: two slopes, a ridge, tile courses, eave ends; old walls are mossy and have lost tiles */
function cap(g: G, x: number, y0: number, y1: number, wy0: number, face: number, old: boolean) {
  const T = old ? OLD_TILE : NEW_TILE, h = y1 - y0, n = W.capN, sw = (n - 4) / 2, re = 1 + sw + 2; // slope width, east slope start
  px(g, x, y0, n, h, K.ink);
  px(g, x + 1, y0, sw, h, T.w); px(g, x + 1 + sw, y0, 2, h, T.ridge); px(g, x + re, y0, sw, h, T.e);
  px(g, x + 1 + sw, y0, 1, h, T.ridgeHi);
  for (let j = Math.ceil(wy0 / 6) * 6; j < wy0 + h; j += 6) { // tile courses, with their lit lower edges and eave tile ends
    const ly = y0 + j - wy0;
    px(g, x + 1, ly, sw, 1, T.line); px(g, x + re, ly, sw, 1, T.line);
    px(g, x + 1, ly + 1, sw, 1, alpha(K.white, 0.1)); px(g, x, ly + 2, 1, 3, T.ridge); px(g, x + n - 1, ly + 2, 1, 3, T.line);
  }
  for (let j = Math.ceil(wy0 / 12) * 12; j < wy0 + h; j += 12) { px(g, x + 1 + (sw >> 1), y0 + j - wy0, 1, 6, T.line); px(g, x + re + (sw >> 1), y0 + j - wy0, 1, 6, T.line); }
  if (old) {
    speckle(g, x + 1, y0, n - 2, h, SK.mossHi, 0.14, face + 9, 2);
    for (let s = Math.floor(wy0 / 40); s <= Math.floor((wy0 + h) / 40); s++) if (hash2(s, face + 21) < 0.28) { // a broken cap: tiles slipped off
      const ly = y0 + s * 40 + 6 + Math.floor(hash2(face, s) * 20) - wy0, side = hash2(s, 8) < 0.5 ? 1 : re; if (ly < y0 - 8 || ly > y1) continue;
      px(g, x + side, ly, sw, 8, SK.ashLo); px(g, x + side + 1, ly + 1, sw - 2, 6, K.ink); speckle(g, x + side, ly, sw, 8, SK.moss, 0.4, s, 1);
    }
  }
}

/** a roofed pier: the wall steps out into a pilaster with its own little hip roof and a south face, so from above the
 *  wall shows a tall front like the buildings do (stone foot, lime panel, timber frame, eave, tiles) */
function pier(g: G, x: number, y: number, old: boolean) {
  const T = old ? OLD_TILE : NEW_TILE, w = 62, roofH = 14, faceH = 16;
  hipRoof(g, x + 1, y, w, roofH, 0.5, 14, T);
  const fy = y + roofH; // the south face
  px(g, x + 1, fy, w, faceH, K.ink);
  px(g, x + 2, fy, w - 2, faceH - 1, old ? mix(SK.paper, SK.ashLo, 0.3) : SK.paper);
  px(g, x + 2, fy, w - 2, 3, alpha(K.night, 0.55)); // the eave's shadow
  px(g, x + 2, fy + faceH - 5, w - 2, 4, SK.stone); px(g, x + 2, fy + faceH - 5, w - 2, 1, lt(SK.stone, 0.2));
  for (const px0 of [x + 3, x + w - 7]) { px(g, px0 - 1, fy, 7, faceH - 1, K.ink); px(g, px0, fy, 5, faceH - 5, K.woodLo); px(g, px0, fy, 1, faceH - 5, K.wood); }
  px(g, x + 14, fy + 5, w - 28, 2, K.woodLo); // lintel between the posts
  px(g, x + 1, fy + faceH, w, 2, alpha(K.night, 0.5));
}

/** the wall over a rect of the west edge (x256-257): world-anchored along y, painted only where the piece is */
function wallRoof(g: G, x: number, y: number, w: number, h: number, old: boolean) {
  const v = visible(g, x, y, w, h); if (!v) return;
  const { oy } = sanctuaryOrigin(), y0 = Math.floor(v.y0), y1 = Math.ceil(v.y1), wy0 = oy + y0;
  g.save(); g.beginPath(); g.rect(v.x0, y0, v.x1 - v.x0, y1 - y0); g.clip();
  px(g, x, y0, w, y1 - y0, K.ink);
  stoneFace(g, x + W.stoneW, y0, y1, wy0, 1, old, false);
  stoneFace(g, x + W.stoneW, y0, y1, wy0, 1, old, false, W.stoneN);
  plasterFace(g, x + W.plasterW, W.plasterN, y0, y1, wy0, 1, old, false);
  px(g, x + W.shadeW, y0, W.shadeN, y1 - y0, alpha(K.night, 0.55));
  cap(g, x + W.capW, y0, y1, wy0, 3, old);
  px(g, x + W.shadeE, y0, W.shadeN, y1 - y0, alpha(K.night, 0.62));
  plasterFace(g, x + W.plasterE, W.plasterEN, y0, y1, wy0, 2, old, true);
  stoneFace(g, x + W.stoneE, y0, y1, wy0, 2, old, true, W.stoneEN);
  // the base of the plaster where it meets the stone, and the eave's drip line on the plaster
  px(g, x + W.plasterW, y0, 1, y1 - y0, alpha(K.night, 0.45)); px(g, x + W.plasterE + W.plasterEN - 1, y0, 1, y1 - y0, alpha(K.night, 0.55));
  px(g, x, y0, 1, y1 - y0, K.ink); px(g, x + w - 1, y0, 1, y1 - y0, K.ink);
  for (let q = Math.floor((wy0 - 30) / PIER_EVERY); q <= Math.floor((wy0 + (y1 - y0)) / PIER_EVERY); q++) { // roofed piers
    const ly = y0 + q * PIER_EVERY + 80 - wy0; if (ly + 32 < y0 || ly > y1) continue;
    pier(g, x, ly, old);
  }
  g.restore();
  // the wall's shadow falls inside (light from the top-left), thinning over the forest floor; this is why the wall is
  // listed after the forest in the scene files
  for (let k = 0; k < 24; k++) px(g, x + w + k, y0, 1, y1 - y0, alpha(K.night, 0.36 * (1 - k / 24) ** 1.5));
}
registerRoof('sanctWall', { parapet: false, paint: (g, x, y, w, h) => wallRoof(g, x, y, w, h, false) });
registerRoof('sanctWallOld', { parapet: false, paint: (g, x, y, w, h) => wallRoof(g, x, y, w, h, true) });

// ---- the gate piers beside the road opening (y84-86): a lodge with its front on the road, and its roofed twin south of it
registerRoof('sanctGateRoof', { parapet: false, paint: (g, x, y, w, h) => {
  px(g, x, y, w, h, K.ink);
  hipRoof(g, x + 1, y + 1, w - 2, h - 2, 0.5, Math.floor((w - 2) / 2), NEW_TILE);
  // the gilded finial and the corp's sensor on the ridge
  const cx = x + w / 2, cy = y + Math.round(h / 2);
  px(g, cx - 3, cy - 3, 6, 6, K.ink); px(g, cx - 2, cy - 2, 4, 4, SK.gold); px(g, cx - 2, cy - 2, 4, 1, SK.goldHi);
  px(g, cx + 6, cy - 10, 2, 8, K.ink); px(g, cx + 6, cy - 12, 2, 2, K.cyan);
} });
registerFront('sanctGateLodge', { wall: SK.paper, upper: 'none' }, ({ g, x, y, w, h, L }) => {
  px(g, x, y, w, h, SK.paper); px(g, x, y, 1, h, K.ink); px(g, x + w - 1, y, 1, h, K.ink); speckle(g, x, y, w, h, alpha(SK.stoneLo, 0.5), 0.05, 4, 2);
  px(g, x, y, w, 12, K.ink); px(g, x, y + 12, w, 4, alpha(K.night, 0.5)); // eave shadow
  for (let i = x + 3; i < x + w - 3; i += 8) { px(g, i, y + 4, 5, 5, K.woodHi); px(g, i, y + 4, 5, 1, lt(K.woodHi, 0.3)); } // rafter ends
  const base = y + h - 16;
  for (const px0 of [x + 2, x + w - 11]) { px(g, px0 - 1, y + 16, 11, base - y - 16, K.ink); px(g, px0, y + 16, 9, base - y - 16, K.woodLo); px(g, px0, y + 16, 3, base - y - 16, K.wood); } // posts
  px(g, x + 8, y + 18, w - 16, 4, K.woodLo); px(g, x + 8, y + 18, w - 16, 1, K.wood); // upper beam
  // the nameplate board
  const s = 'KANEMORI', tw = textW(s, 1), bx = x + (w - tw) / 2 - 3;
  px(g, bx - 1, y + 24, tw + 8, 13, K.ink); px(g, bx, y + 25, tw + 6, 11, K.woodLo); px(g, bx, y + 25, tw + 6, 1, K.wood);
  text(g, s, bx + 3, y + 28, SK.goldHi, 1);
  // a corp card reader and its scanner strip: the checkpoint's security is still here
  px(g, x + 14, y + 42, w - 28, 3, K.cyan); px(g, x + 14, y + 45, w - 28, 1, alpha(K.cyan, 0.4)); L(x + 14, y + 40, w - 28, 8, K.cyan, 0.5, true);
  lantern(g, x + w / 2 - 4, y + 48 - 1, K.warm, L);
  px(g, x + 2, base, w - 4, 16, SK.stone); // stone footing
  for (let j = 0; j < 16; j += 8) for (let i = (j / 8 % 2) * -6; i < w - 4; i += 14) {
    const v = hash2(x + i, y + j), c = v < 0.3 ? SK.stoneHi : v < 0.75 ? SK.stone : SK.stoneLo, a0 = x + 2 + Math.max(0, i);
    px(g, a0, base + j, Math.min(13, x + w - 2 - a0), 8, K.ink); px(g, a0 + 1, base + j + 1, Math.min(11, x + w - 3 - a0), 6, c); px(g, a0 + 1, base + j + 1, Math.min(11, x + w - 3 - a0), 1, lt(c, 0.18));
  }
  px(g, x, y + h - 3, w, 3, alpha(K.night, 0.55));
});
